import { defineStore } from 'pinia'

import { actions } from '../constants'
import { useBudgetActionsStore } from './budget-actions'

import type {
  PredictionState,
  Prediction,
  PredictionValues,
  PredictionsByAccount,
} from '../types'

export const usePredictionsStore = defineStore('predictions', {
  state: (): PredictionState => ({
    reviewedRows: [],
    prediction: {
      accounts: [],
      status: null,
    },
  }),
  getters: {
    needsPrediction(state: PredictionState): boolean {
      return state.prediction.status !== 'success'
    },

    getAllAvailablePredictionTypes(state): string[] {
      return state.prediction.accounts.reduce((allTypes, account) => {
        account.predictions.forEach((prediction) => {
          if (allTypes.indexOf(prediction.type) === -1) {
            allTypes.push(prediction.type)
          }
        })

        return allTypes
      }, [] as string[])
    },

    getPredictionsPerAccount(state): Record<string, Prediction[]> {
      const accounts = state.prediction.accounts || []

      return accounts.reduce((acc, account) => {
        acc[account.id] = account.predictions
        return acc
      }, {} as Record<string | number, Prediction[]>)
    },

    getSuggestedPredictionPerAccount: (
      state
    ): Record<string, Prediction | undefined> => {
      return state.prediction.accounts.reduce((acc, account) => {
        const prediction = account.predictions.find(
          (prediction) => prediction.type === account.suggested_prediction
        )

        acc[account.id] = prediction

        return acc
      }, {} as Record<string | number, Prediction | undefined>)
    },

    getSuggestedPredictions(state): Record<string, PredictionValues[]> {
      const predictionableAccounts = state.prediction.accounts.filter(
        (account) => !account.leave_empty
      )
      const suggestedPredictionsPerAccount = this
        .getSuggestedPredictionPerAccount as unknown as Record<
        string,
        Prediction
      >

      return predictionableAccounts.reduce((suggestedPredictions, account) => {
        const predictionValues = suggestedPredictionsPerAccount[account.id]

        if (predictionValues && predictionValues.values) {
          suggestedPredictions[account.id] = predictionValues.values
        }

        return suggestedPredictions
      }, {} as Record<string, PredictionValues[]>)
    },

    getSuggestedPredictionActions(
      state
    ): Record<string, { type: string; value: string }> {
      const suggestedPredictionsPerAccount = this
        .getSuggestedPredictionPerAccount as unknown as Record<
        string,
        Prediction
      >

      return state.prediction.accounts.reduce(
        (suggestedPredictionActions, account) => {
          const prediction = suggestedPredictionsPerAccount[account.id]

          if (prediction) {
            suggestedPredictionActions[account.id] = {
              type: account.suggested_prediction,
              value: prediction.yoy_percentage + '%',
            }
          }

          return suggestedPredictionActions
        },
        {} as Record<string, { type: string; value: string }>
      )
    },

    predictionTypesNeedingReviewPerAccount(state): Record<string, string[]> {
      const accounts = state.prediction.accounts || []

      return accounts.reduce((acc, account) => {
        if (
          account.review_data &&
          !state.reviewedRows.includes(String(account.id))
        ) {
          acc[account.id] = account.predictions.map(
            (prediction) => prediction.type
          )
        }

        return acc
      }, {} as Record<string, string[]>)
    },
  },
  actions: {
    resetPrediction(): void {
      this.prediction = {
        accounts: [],
        status: null,
      }
      this.reviewedRows = []
    },
    setAccountAsReviewed(elementId: string): void {
      this.reviewedRows.push(elementId)
    },
    setPrediction(payload: PredictionsByAccount): void {
      const budgetActionsStore = useBudgetActionsStore()

      const registeredActions = Object.keys(actions)

      const accounts = payload.accounts
        .filter((account) => account.id in budgetActionsStore.previousActions)
        .map((account) => ({
          ...account,
          predictions: account.predictions.filter((prediction) =>
            registeredActions.includes(prediction['type'])
          ),
        }))

      this.prediction.accounts = accounts
      this.prediction.status = payload.status
    },
  },
})
